import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { ConnectionStatus } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { useInView } from 'react-intersection-observer';
import {
  useGetPayerMixAnalysisQuery,
  useInvalidateGetPayerMixAnalysisQuery,
} from '@frontend/api-insurance-verification-adapter';
import { Missing } from '@frontend/empty-states#svg';
import { DashboardWidget, type DashboardWidgetFC } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InsuranceIllustration } from '@frontend/insurance-widget-assets#svg';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Chip, ContentLoader, Table, TableColumnConfig, Text, Truncated } from '@frontend/design-system';
import { useInsurancePayerConnectionShallowStore } from '../../stores/use-insurance-payer-connection-store';
import { insuranceWidgetsDashboardTrackingIds } from '../../tracking-ids';
import { getConnectionStatusLabel, getTopPayerMixDetailsFromRecommendations, TOP_PAYER_MIX_LIMIT } from '../helpers';
import { CoverageMeter } from './coverage-meter';
import { getStatusChipStyles } from './helpers';

type PayerMixTableRow = {
  id: string;
  payerName: string;
  patientsImpacted: number;
  coverageIncrease: number;
  connectionStatus: ConnectionStatus | undefined;
  isConnected: boolean;
  isWebSupported: boolean;
  partnerPayerId: string;
  partnerWebsiteId: string;
};

/**
 * @dashboard-widget
 *
 * id: unlock-more-patient-coverage-widget
 * title: Insurance Eligibility Payer Coverage
 * description: Improve website eligibility coverage by connecting payer credentials.
 * icon: shield
 */
export const UnlockMorePatientCoverageWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('dashboard');
  const { selectedLocationIds } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();
  const locationId = selectedLocationIds[0] ?? '';
  const { ref, inView } = useInView({ triggerOnce: true });

  const { setPayerConnectionTab, setPayerConnectionPayerID, setPayerConnectionPayerWebsiteID } =
    useInsurancePayerConnectionShallowStore(
      'setPayerConnectionTab',
      'setPayerConnectionPayerID',
      'setPayerConnectionPayerWebsiteID'
    );

  const queryRequest = useMemo(
    () => ({
      locationId,
    }),
    [locationId]
  );

  const { data, isLoading, isFetching } = useGetPayerMixAnalysisQuery({
    request: queryRequest,
    options: {
      enabled: !!locationId,
    },
  });

  const invalidateGetPayerMixAnalysisQuery = useInvalidateGetPayerMixAnalysisQuery();

  const handleRefreshClick = useCallback(async () => {
    if (!locationId) {
      return;
    }
    invalidateGetPayerMixAnalysisQuery(queryRequest);
  }, [invalidateGetPayerMixAnalysisQuery, locationId, queryRequest]);

  const summary = data?.summary;
  const isWidgetLoading = isLoading || isFetching;
  const isPotentialCoverageZero = (summary?.potentialWebCoveragePercentage ?? 0) === 0;
  const currentCoverageColor = isPotentialCoverageZero ? theme.colors.neutral50 : theme.colors.critical50;

  const potentialCoverageColor = isPotentialCoverageZero ? theme.colors.neutral50 : theme.colors.success50;

  const topPayerMixDetails = getTopPayerMixDetailsFromRecommendations(
    data?.recommendedConnections,
    data?.payerMixDetails,
    TOP_PAYER_MIX_LIMIT
  );

  useEffect(() => {
    if (inView) {
      pendo.track(insuranceWidgetsDashboardTrackingIds.unlockCoverageWidgetVisible);
    }
  }, [inView]);

  const navigateToPayerConnectionManagement = useCallback(
    (payload?: { payerId?: string; payerWebsiteId?: string; webSupported?: boolean }) => {
      setPayerConnectionTab(payload?.webSupported ? 'websites' : '');
      setPayerConnectionPayerID(payload?.payerId || '');
      setPayerConnectionPayerWebsiteID(payload?.payerWebsiteId || '');

      if (selectedLocationIds.length <= 1) {
        navigate({ to: '/insurance/payer-connection-management' });
        return;
      }

      if (locationId) {
        navigate({
          to: '/insurance/payer-connection-management/:locationId',
          params: { locationId },
        });
      } else {
        navigate({ to: '/insurance/payer-connection-management' });
      }
    },
    [
      setPayerConnectionTab,
      setPayerConnectionPayerID,
      setPayerConnectionPayerWebsiteID,
      selectedLocationIds,
      locationId,
      navigate,
    ]
  );

  const tableData = useMemo<PayerMixTableRow[]>(
    () =>
      topPayerMixDetails.map((row, index) => ({
        id: row.payerId || row.payerName || String(index),
        payerName: row.payerName || t('Unknown'),
        patientsImpacted: row.appointmentCount ?? 0,
        coverageIncrease: Number((row.appointmentPercentage ?? 0).toFixed(1)),
        isConnected: !!row.connected,
        connectionStatus: row?.connectionStatus,
        isWebSupported: !!row.webSupported,
        partnerPayerId: row.partnerPayerId || '',
        partnerWebsiteId: row.partnerWebsiteId || '',
      })),
    [topPayerMixDetails, t]
  );

  const tableColumns = useMemo<TableColumnConfig<PayerMixTableRow>[]>(
    () => [
      {
        id: 'priority',
        Header: t('Priority'),
        width: 60,
        disableSortBy: true,
        HeaderLabel: () => <Text weight='bold'>{t('Priority')}</Text>,
        cellRenderer: (_cellValue, _rowData, rowObject) => <Text weight='bold'>{(rowObject?.index ?? 0) + 1}</Text>,
      },
      {
        id: 'payerName',
        Header: t('Payer'),
        accessor: 'payerName',
        width: 250,
        disableSortBy: true,
        cellRenderer: (payerName, rowData) => {
          const statusLabel = getConnectionStatusLabel(rowData?.connectionStatus);
          return (
            <div css={payerNameStyles}>
              <Truncated as={Text} weight='bold' css={payerNameTextStyles}>
                {payerName}
              </Truncated>
              {statusLabel && (
                <Chip css={[statusChipStyles, getStatusChipStyles(rowData?.connectionStatus)]}>{statusLabel}</Chip>
              )}
            </div>
          );
        },
      },
      {
        id: 'patientsImpacted',
        Header: t('Patients Impacted'),
        accessor: 'patientsImpacted',
        width: 150,
        disableSortBy: true,
        cellRenderer: (cellValue) => (
          <div css={css({ display: 'flex', flexDirection: 'row', gap: theme.spacing(1) })}>
            <Text weight='bold'>{cellValue}</Text>
            <Text>{t('Patients')}</Text>
          </div>
        ),
      },
      {
        id: 'coverageIncrease',
        Header: t('Coverage Increase'),
        accessor: 'coverageIncrease',
        width: 120,
        disableSortBy: true,
        cellRenderer: (cellValue) => <Text weight='bold'>+{cellValue}%</Text>,
      },
      {
        id: 'connect',
        Header: '',
        width: 100,
        disableSortBy: true,
        cellRenderer: (_cellValue, rowData) => (
          <Button
            variant='tertiary'
            css={connectButtonStyles}
            trackingId={insuranceWidgetsDashboardTrackingIds.unlockCoverageTableConnectButton}
            onClick={() =>
              navigateToPayerConnectionManagement({
                payerId: rowData?.partnerPayerId,
                payerWebsiteId: rowData?.partnerWebsiteId,
                webSupported: rowData?.isWebSupported,
              })
            }
            iconName='external-link-small'
          >
            {t('Connect')}
          </Button>
        ),
      },
    ],
    [navigateToPayerConnectionManagement, t]
  );

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        <div css={headerControlsStyles}>
          <Button
            size='medium'
            variant='tertiary'
            aria-label={t('Refresh insurance eligibility coverage widget data')}
            iconName='update-small'
            css={refreshIconStyles}
            disabled={!locationId || isWidgetLoading || isFetching}
            onClick={handleRefreshClick}
            trackingId={insuranceWidgetsDashboardTrackingIds.unlockCoverageRefreshButton}
          />
          <Text size='medium' css={headerTextStyles}>
            <Icon name='clock-small' />
            <span>{t('Next 30 Days')}</span>
          </Text>
        </div>
      </DashboardWidget.Header>
      <DashboardWidget.Content css={contentStyles}>
        {isWidgetLoading && <ContentLoader show />}
        {!isWidgetLoading && !!summary && (
          <>
            {!isPotentialCoverageZero && (
              <div css={eligibilityBannerStyles} ref={ref}>
                <InsuranceIllustration width={48} height={48} css={{ flexShrink: 0 }} />
                <div css={{ flex: 1, minWidth: 0 }}>
                  <Text weight='bold'>
                    {t('Increase website eligibility coverage for {{count}} patients.', {
                      count: summary.totalAppointments ?? 0,
                    })}
                  </Text>
                  <Text size='small' color='light'>
                    {t('Connect your top payer websites to improve eligibility')}
                  </Text>
                </div>
                <Button
                  variant='tertiary'
                  trackingId={insuranceWidgetsDashboardTrackingIds.unlockCoverageConnectPayersButton}
                  css={connectPayersButtonStyles}
                  iconName='external-link-small'
                  onClick={() => navigateToPayerConnectionManagement()}
                >
                  {t('Connect Payers')}
                </Button>
              </div>
            )}

            <div css={coverageSummaryStyles}>
              <div css={{ flex: 1 }}>
                <CoverageMeter
                  label={t('Current Web Coverage')}
                  value={summary.currentWebCoveragePercentage ?? 0}
                  color={currentCoverageColor}
                  sublabel={t('Eligibility via payer websites')}
                />
              </div>
              <div css={{ flex: 1 }}>
                <CoverageMeter
                  label={t('Potential Coverage')}
                  value={summary.potentialWebCoveragePercentage ?? 0}
                  color={potentialCoverageColor}
                  sublabel={t('By connecting top payers')}
                />
              </div>
            </div>

            {isPotentialCoverageZero ? (
              <div css={emptyStateContainerStyles}>
                <Missing css={emptyStateIllustrationStyles} />
                <Text size='small' color='subdued'>
                  {t('No Data Found')}
                </Text>
              </div>
            ) : (
              <Table<PayerMixTableRow>
                colConfig={tableColumns}
                data={tableData}
                wrapperStyle={tableWrapperStyles}
                tableStyle={tableStyles}
              />
            )}
          </>
        )}
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

UnlockMorePatientCoverageWidget.config = {
  size: {
    large: 'large-wide',
    medium: 'large-wide',
    small: 'large-narrow',
    extraSmall: 'large-narrow',
  },
  feature: 'insurance',
};

const contentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(1),
  gap: theme.spacing(1.5),
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

const headerControlsStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
});

const headerTextStyles = css({
  color: theme.colors.neutral70,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5),
  gap: theme.spacing(0.5),
});

const refreshIconStyles = css({
  '& svg': {
    width: 20,
    height: 20,
    color: 'var(--fabric-color-neutral-70)',
  },
});

const coverageSummaryStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  padding: theme.spacing(0, 2),
});

const connectPayersButtonStyles = css({
  alignSelf: 'center',
  whiteSpace: 'nowrap',
});

const connectButtonStyles = css({
  padding: 0,
  minWidth: 0,
  '& svg': {
    flexShrink: 0,
  },
});

const tableWrapperStyles = css({
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  overflow: 'auto',
  boxShadow: 'none',
  padding: 0,
});

const tableStyles = css({
  '.table__header': {
    backgroundColor: `${theme.colors.neutral5}`,
    minHeight: theme.spacing(4.5),
  },
  '.table__row': {
    minHeight: theme.spacing(5),
    height: theme.spacing(5),
  },
  '.table__cell': {
    minHeight: theme.spacing(5),
  },
});

const payerNameStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 0,
});

const payerNameTextStyles = css({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const statusChipStyles = css({
  width: 'fit-content',
  height: theme.spacing(2),
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0, 0.5),
});

const eligibilityBannerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.secondary.seaweed50}`,
  backgroundColor: theme.colors.secondary.seaweed5,
});

const emptyStateContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 0, 1),
});

const emptyStateIllustrationStyles = css({
  height: theme.spacing(22),
  width: 'auto',
});
